import React from 'react';
import { Link } from 'react-router-dom';
import { DOMAINS, getDomainById, PASSING_SCORE } from '../../../shared/constants/domains';

/**
 * Exam results screen: score, pass/fail, domain breakdown.
 */
export default function ResultsScreen({ examRecord, questions }) {
  if (!examRecord) return null;

  const { score } = examRecord;
  const passed = score.passed;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Hero score */}
      <div className="text-center mb-8">
        <div className={`text-6xl font-bold mb-2 ${passed ? 'text-green-600' : 'text-red-600'}`}>
          {score.percentage}%
        </div>
        <div className={`text-xl font-semibold mb-1 ${passed ? 'text-green-600' : 'text-red-600'}`}>
          {passed ? 'PASS' : 'FAIL'}
        </div>
        <p className="text-gray-500">
          {score.total_correct} of {score.total_questions} correct
          {' • '}
          Estimated: {score.estimated_scaled}/1000 (passing: {PASSING_SCORE})
        </p>
      </div>

      {/* Domain breakdown */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Domain Breakdown</h3>
        <div className="space-y-3">
          {DOMAINS.map(domain => {
            const d = score.per_domain?.[domain.id] || { correct: 0, total: 0, percentage: 0 };
            if (d.total === 0) return null;

            const pctColor = d.percentage >= 80 ? 'text-green-600' :
              d.percentage >= 60 ? 'text-yellow-600' : 'text-red-600';

            return (
              <div key={domain.id} className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: domain.color }} />
                <span className="text-sm text-gray-700 w-40 truncate">{domain.name}</span>
                <div className="flex-1 bg-gray-100 rounded-full h-3">
                  <div
                    className="h-3 rounded-full transition-all"
                    style={{
                      width: `${d.percentage}%`,
                      backgroundColor: d.percentage >= 80 ? '#16a34a' : d.percentage >= 60 ? '#ca8a04' : '#dc2626',
                    }}
                  />
                </div>
                <span className={`text-sm font-medium w-12 text-right ${pctColor}`}>
                  {d.percentage}%
                </span>
                <span className="text-xs text-gray-400 w-14 text-right">
                  {d.correct}/{d.total}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3 justify-center">
        <Link
          to={`/exam/review/${examRecord.id}`}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
        >
          Review All Answers
        </Link>
        <Link
          to={`/exam/review/${examRecord.id}?filter=wrong`}
          className="px-6 py-3 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100"
        >
          Review Wrong Answers ({score.total_questions - score.total_correct})
        </Link>
        <Link
          to="/exam"
          className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
        >
          Back to Exam Home
        </Link>
      </div>
    </div>
  );
}
